import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CaretDown } from "@phosphor-icons/react";
import Layout from "@/components/Layout";
import { disciplines as mockDisciplines } from "@/data/mockData";
import { useBrand } from "@/context/BrandContext";

const visuals = [
  "https://images.unsplash.com/photo-1764345933694-7d4eb49ef266?w=1400&q=80",
  "https://images.pexels.com/photos/9002789/pexels-photo-9002789.jpeg",
  "https://images.pexels.com/photos/24408960/pexels-photo-24408960.jpeg",
  "https://images.unsplash.com/photo-1761078739072-a10f63f3e478?w=1400&q=80",
  "https://images.unsplash.com/photo-1512820790803-83ca734da794?w=1400&q=80",
  "https://images.unsplash.com/photo-1528895709218-bed5456e2c42?w=1400&q=80",
];

export default function Disciplines() {
  const { disciplines: live } = useBrand();
  const disciplines =
    live && live.length
      ? live
      : mockDisciplines.map((d) => ({ ...d, sub_categories: [] }));
  const [openSlug, setOpenSlug] = useState(null);

  const toggle = (slug) =>
    setOpenSlug((cur) => (cur === slug ? null : slug));

  return (
    <Layout wide>
      <div data-testid="disciplines-page">
        <header className="p-4 md:p-8 border-b border-[#27272A]">
          <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-2">
            ÍNDICE / {String(disciplines.length).padStart(2, "0")} TERRITORIOS
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter uppercase leading-none">
            Mapa del <span className="text-[#FF3E00]">oficio</span>.
          </h1>
          <p className="font-mono text-sm text-[#A1A1AA] mt-5 max-w-xl leading-relaxed">
            // HAZ CLIC EN UNA DISCIPLINA PARA VER SUS GÉNEROS. NAVEGA POR SUBCATEGORÍA O ENTRA AL DIRECTORIO COMPLETO.
          </p>
        </header>

        <section className="grid grid-cols-1 md:grid-cols-2">
          {disciplines.map((d, i) => {
            const isOpen = openSlug === d.slug;
            const subs = d.sub_categories || [];
            return (
              <div
                key={d.slug}
                data-testid={`discipline-${d.slug}`}
                className={`group relative border-b border-r border-[#27272A] transition-all duration-500 ${
                  isOpen ? "md:col-span-2" : ""
                }`}
              >
                {/* Clickable hero */}
                <button
                  onClick={() => toggle(d.slug)}
                  data-testid={`discipline-toggle-${d.slug}`}
                  className={`relative block w-full text-left overflow-hidden ${
                    isOpen
                      ? "aspect-[16/5] md:aspect-[16/4]"
                      : "aspect-[4/3] md:aspect-[5/4]"
                  } transition-[aspect-ratio] duration-500`}
                >
                  <img
                    src={visuals[i % visuals.length]}
                    alt={d.name}
                    className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 scale-[1.02] group-hover:scale-[1.08]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />

                  <div className="absolute top-5 left-5 right-5 flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#F4F4F5]">
                      {String(i + 1).padStart(2, "0")} /{" "}
                      {String(disciplines.length).padStart(2, "0")}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] bg-black/70 backdrop-blur-sm border border-[#27272A] px-2 py-1 text-[#FF3E00]">
                      {subs.length} géneros
                    </span>
                  </div>

                  <div className="absolute bottom-0 inset-x-0 p-6 md:p-10 flex items-end justify-between gap-4">
                    <div>
                      <h2 className="font-display text-4xl md:text-6xl font-black tracking-tighter uppercase leading-none">
                        {d.name}
                      </h2>
                      <p className="font-mono text-[11px] text-[#FF3E00] tracking-wider mt-3">
                        {d.tag}
                      </p>
                    </div>
                    <CaretDown
                      size={32}
                      weight="bold"
                      className={`text-white shrink-0 transition-transform duration-300 ${
                        isOpen ? "rotate-180 text-[#FF3E00]" : ""
                      }`}
                    />
                  </div>
                </button>

                {/* Expandable subcategories drawer */}
                {isOpen ? (
                  <div
                    data-testid={`subcats-panel-${d.slug}`}
                    className="bg-[#050505] border-t border-[#FF3E00] p-6 md:p-10"
                  >
                    <div className="flex items-center justify-between mb-5">
                      <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.3em]">
                        // GÉNEROS DE {d.name}
                      </div>
                      <Link
                        to={`/explorar?discipline=${d.slug}`}
                        className="font-mono text-[10px] uppercase tracking-[0.22em] bg-[#FF3E00] text-white px-3 py-1.5 inline-flex items-center gap-1.5 hover:bg-white"
                      >
                        Ver todes los artistas
                        <ArrowUpRight size={12} weight="bold" />
                      </Link>
                    </div>
                    {subs.length === 0 ? (
                      <p className="font-mono text-[11px] text-[#525252] uppercase tracking-[0.22em]">
                        · sin subcategorías aún ·
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {subs.map((sc, idx) => (
                          <Link
                            key={sc.slug}
                            to={`/explorar?discipline=${d.slug}&sub=${sc.slug}`}
                            data-testid={`genre-${sc.slug}`}
                            className="group/genre block border border-[#27272A] bg-[#0a0a0a] hover:border-[#FF3E00] hover:bg-[#121212] p-4 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.25em]">
                                {String(idx + 1).padStart(2, "0")}
                              </div>
                              <ArrowUpRight
                                size={14}
                                weight="bold"
                                className="text-[#525252] group-hover/genre:text-[#FF3E00] group-hover/genre:rotate-45 transition-all"
                              />
                            </div>
                            <div className="font-display font-black text-lg uppercase tracking-tight mt-2 leading-tight">
                              {sc.name}
                            </div>
                            <div className="font-mono text-[9px] text-[#525252] tracking-wider mt-1">
                              /{sc.slug}
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </section>
      </div>
    </Layout>
  );
}
